import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Search, X, Copy, Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { exportToExcel, exportToCsv } from '@/lib/exportExcel';
import { RestaurantSelector } from '../components/RestaurantSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------------------------------------------------------------- */
/*  Reusable list view used by BOTH tabs                                */
/* -------------------------------------------------------------------- */
const CategoryList = ({
  endpoint,           // e.g. '/categories' or '/admin-categories'
  withRestaurant,     // include restaurant column / selector
  withStatusToggle,   // show inline status toggle switch on the table
  testIdPrefix,       // for unique data-testids per tab
  emptyHint,
  title,
  subtitle
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true); // first load only (full-page spinner)
  const [fetching, setFetching] = useState(false); // subsequent page/filter changes (inline)
  const [togglingId, setTogglingId] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [restaurantFilter, setRestaurantFilter] = useState('all');
  // Full restaurant list for the filter dropdown — fetched independently of
  // the (now paginated) category list, which only ever holds one page.
  const [allRestaurants, setAllRestaurants] = useState([]);

  // Pagination
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [current, setCurrent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const emptyForm = {
    name: '',
    resturantId: '',
    parentId: null,
    priority: 0,
    isHome: false,
    status: true
  };
  const [formData, setFormData] = useState(emptyForm);

  // Full restaurant list for the filter dropdown (restaurant-scoped tab only)
  useEffect(() => {
    if (!withRestaurant) return;
    axios
      .get(`${API}/restaurants`, { params: { limit: 1000 } })
      .then((r) => {
        const list = Array.isArray(r.data?.data) ? r.data.data : (Array.isArray(r.data) ? r.data : []);
        setAllRestaurants(
          list.map((x) => ({ id: x.id, name: x.name })).sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [withRestaurant]);

  // Debounce the search box — reset to page 1 once the query settles.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  // Re-fetch whenever page, search, or the restaurant filter changes.
  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line
  }, [page, debouncedQuery, restaurantFilter]);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const fetchItems = async () => {
    setFetching(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (debouncedQuery) params.q = debouncedQuery;
      if (withRestaurant && restaurantFilter !== 'all') params.resturantId = restaurantFilter;
      const res = await axios.get(`${API}${endpoint}`, { params });
      const { data = [], pagination = {} } = res.data || {};
      setItems(data);
      setTotal(pagination.total || 0);
      setTotalPages(pagination.totalPages || 1);
    } catch {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  // ── Export (all pages matching the current search / restaurant filter) ──
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const all = [];
      const LIMIT = 1000;
      for (let p = 1; p <= 50; p++) {
        const params = { page: p, limit: LIMIT };
        if (debouncedQuery) params.q = debouncedQuery;
        if (withRestaurant && restaurantFilter !== 'all') params.resturantId = restaurantFilter;
        const res = await axios.get(`${API}${endpoint}`, { params });
        const { data = [], pagination = {} } = res.data || {};
        all.push(...data);
        if (p >= (pagination.totalPages || 1) || data.length === 0) break;
      }
      if (!all.length) {
        toast.error('No categories to export');
        return;
      }

      const byId = new Map(all.map((c) => [String(c.id || c._id), c]));
      const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-IN') : '');
      const rows = all.map((c) => {
        const parentId = c.parentId ? String(c.parentId._id || c.parentId) : '';
        const row = {
          'Category ID': String(c.id || c._id),
          'Category Name': c.name || '',
          'Parent Category ID': parentId,
          'Parent Category Name': parentId ? (byId.get(parentId)?.name || '') : '',
          'Type': parentId ? 'Sub-category' : 'Category',
        };
        if (withRestaurant) {
          row['Restaurant ID'] = c.resturant?.id || c.resturantId || '';
          row['Restaurant Name'] = c.resturant?.name || '';
        }
        row['Priority'] = Number(c.priority || 0);
        row['Status'] = c.status === false ? 'Inactive' : 'Active';
        if (!withRestaurant) row['Show on Home'] = c.isHome ? 'Yes' : 'No';
        row['Slug'] = c.slug || '';
        row['Image URL'] = c.image || '';
        row['Created At'] = fmtDate(c.createdAt);
        return row;
      });

      const restName = withRestaurant && restaurantFilter !== 'all'
        ? (allRestaurants.find((r) => r.id === restaurantFilter)?.name || 'restaurant')
        : null;
      const base = (withRestaurant ? 'restaurant-categories' : 'categories') +
        (restName ? `-${restName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}` : '');
      const ok = format === 'csv'
        ? exportToCsv(rows, base)
        : exportToExcel(rows, base, 'Categories', withRestaurant
            ? [26, 30, 26, 26, 13, 26, 30, 9, 10, 26, 40, 20]
            : [26, 30, 26, 26, 13, 9, 10, 12, 26, 40, 20]);
      if (ok) toast.success(`Exported ${rows.length} categor${rows.length === 1 ? 'y' : 'ies'}`);
    } catch (err) {
      console.error('Category export failed:', err);
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const openCreate = () => {
    setEditMode(false);
    setCurrent(null);
    setImageFile(null);
    setPreviewUrl('');
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (c) => {
    setEditMode(true);
    setCurrent(c);
    setImageFile(null);
    setPreviewUrl(c.image || '');
    setFormData({
      name: c.name || '',
      resturantId: c.resturantId || '',
      parentId: c.parentId || null,
      priority: c.priority || 0,
      isHome: c.isHome || false,
      status: c.status ?? true
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    if (withRestaurant && !formData.resturantId) {
      toast.error('Please select a restaurant');
      return;
    }
    // Guard against duplicate categories being created from a double-click /
    // slow-network double-submit — there was no in-flight guard before, so
    // two POSTs could both succeed and create two rows with the same name.
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('name', formData.name.trim());
      // slug is auto-generated server-side, do NOT send
      if (withRestaurant) payload.append('resturantId', formData.resturantId);
      payload.append('parentId', formData.parentId || '');
      payload.append('isHome', String(!!formData.isHome));
      payload.append('priority', String(formData.priority));
      payload.append('status', formData.status ? 'true' : 'false');
      if (imageFile) payload.append('mediaFile', imageFile);

      if (editMode && current) {
        await axios.patch(`${API}${endpoint}/${current.id}`, payload);
        toast.success('Category updated');
      } else {
        await axios.post(`${API}${endpoint}`, payload);
        toast.success('Category created');
      }

      setShowDialog(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${API}${endpoint}/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleStatus = async (cat, nextStatus) => {
    // Optimistic update
    setItems((prev) =>
      prev.map((it) => (it.id === cat.id ? { ...it, status: nextStatus } : it))
    );
    setTogglingId(cat.id);
    try {
      const payload = new FormData();
      payload.append('status', nextStatus ? 'true' : 'false');
      await axios.patch(`${API}${endpoint}/${cat.id}`, payload);
      toast.success(`Status set to ${nextStatus ? 'Active' : 'Inactive'}`);
    } catch (err) {
      // Revert on error
      setItems((prev) =>
        prev.map((it) =>
          it.id === cat.id ? { ...it, status: !nextStatus } : it
        )
      );
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  // Restaurant filter options (only meaningful in restaurant tab) — sourced
  // from the full restaurant list, not from `items` (which now only ever
  // holds one page of categories).
  const restaurantOptions = allRestaurants;

  // Search + restaurant filtering now happen server-side (see fetchItems)
  // so they apply across the whole dataset, not just the loaded page.

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap justify-between items-end gap-4 mb-6">
        <div>
          <h2
            className="text-2xl font-bold"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {title}
          </h2>
          <p className="text-gray-600 text-sm mt-1">{subtitle}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
        {/* 📤 Export (current search / restaurant filter, all pages) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" disabled={exporting || loading || total === 0}
                    data-testid={`${testIdPrefix}-export-button`}>
              {exporting ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Download size={18} className="mr-2" />}
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs font-normal text-gray-500">
              {total} categor{total === 1 ? 'y' : 'ies'}{withRestaurant && restaurantFilter !== 'all' ? ' · this restaurant' : ''}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExport('xlsx')}>
              <FileSpreadsheet size={16} className="mr-2 text-green-600" /> Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('csv')}>
              <FileText size={16} className="mr-2 text-blue-600" /> CSV (.csv)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button
              onClick={openCreate}
              style={{ backgroundColor: '#C2410C' }}
              className="text-white"
              data-testid={`${testIdPrefix}-create-button`}
            >
              <Plus className="mr-2" size={18} />
              Add Category
            </Button>
          </DialogTrigger>

          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editMode ? 'Edit Category' : 'Create Category'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Category Name *</Label>
                <Input
                  data-testid={`${testIdPrefix}-name-input`}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g. Pizza"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Slug will be auto-generated and made unique.
                </p>
              </div>

              {withRestaurant && (
                <RestaurantSelector
                  value={formData.resturantId}
                  onChange={(id) =>
                    setFormData({ ...formData, resturantId: id })
                  }
                  required
                />
              )}

              <div>
                <Label>Category Image</Label>
                <Input
                  type="file"
                  accept="image/*"
                  data-testid={`${testIdPrefix}-image-input`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (previewUrl?.startsWith('blob:'))
                      URL.revokeObjectURL(previewUrl);
                    setImageFile(file);
                    setPreviewUrl(URL.createObjectURL(file));
                  }}
                  required={!editMode}
                />
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="mt-3 h-32 w-full object-cover rounded border"
                  />
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Is Home</Label>
                  <Select
                    value={String(formData.isHome)}
                    onValueChange={(v) =>
                      setFormData({ ...formData, isHome: v === 'true' })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority <span className="text-xs font-normal text-gray-500">(lower number shows first in the app)</span></Label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: Math.max(0, Number(e.target.value))
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status.toString()}
                    onValueChange={(v) =>
                      setFormData({ ...formData, status: v === 'true' })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
                data-testid={`${testIdPrefix}-submit-button`}
              >
                {isSubmitting
                  ? 'Saving...'
                  : editMode
                    ? 'Update Category'
                    : 'Create Category'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            data-testid={`${testIdPrefix}-search-input`}
            placeholder="Search by name or slug..."
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {withRestaurant && (
          <Select
            value={restaurantFilter}
            onValueChange={(v) => {
              setRestaurantFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Restaurant" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All restaurants</SelectItem>
              {restaurantOptions.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {(search || restaurantFilter !== 'all') && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearch('');
              setDebouncedQuery('');
              setRestaurantFilter('all');
              setPage(1);
            }}
          >
            Clear filters
          </Button>
        )}

        <div className="ml-auto text-sm text-gray-600">
          Showing{' '}
          <span className="font-semibold text-gray-900">
            {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)}
          </span>{' '}
          of {total} categor{total === 1 ? 'y' : 'ies'}
        </div>
      </div>

      {/* TABLE */}
      <div
        className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-opacity ${
          fetching ? 'opacity-60 pointer-events-none' : ''
        }`}
      >
        <div className="overflow-x-auto">
        <table className="w-full" data-testid={`${testIdPrefix}-table`}>
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                Category
              </th>
              {withRestaurant && (
                <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Restaurant
                </th>
              )}
              <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                Image
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                Priority
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                Is Home
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                Status
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                <td className="p-3">
                  <div className="font-medium text-gray-900">{c.name}</div>
                  <div className="text-xs text-gray-500">{c.slug}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-[11px] font-mono text-gray-400">
                      {c.id}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(c.id);
                        toast.success('Category ID copied');
                      }}
                      className="text-gray-400 hover:text-gray-700"
                      title="Copy category ID"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </td>
                {withRestaurant && (
                  <td className="p-3 text-sm">
                    {c.resturant?.name ? (
                      <Badge className="bg-blue-100 text-blue-800 border-0">
                        {c.resturant.name}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                )}
                <td className="p-3">
                  {c.image && (
                    <img
                      src={c.image}
                      alt={c.name}
                      className="w-20 h-10 rounded object-cover"
                    />
                  )}
                </td>
                <td className="p-3">{c.priority}</td>
                <td className="p-3">
                  {String(c.isHome) === 'true' ? 'Yes' : 'No'}
                </td>
                <td className="p-3">
                  {withStatusToggle ? (
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!c.status}
                        disabled={togglingId === c.id}
                        onCheckedChange={(val) => toggleStatus(c, val)}
                        data-testid={`${testIdPrefix}-status-toggle-${c.id}`}
                        className="data-[state=checked]:bg-green-600"
                      />
                      <span
                        className={`text-xs font-medium ${
                          c.status ? 'text-green-700' : 'text-gray-500'
                        }`}
                      >
                        {c.status ? 'On' : 'Off'}
                      </span>
                    </div>
                  ) : (
                    <Badge
                      className={`${
                        c.status
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      } border-0`}
                    >
                      {c.status ? 'Active' : 'Inactive'}
                    </Badge>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`${testIdPrefix}-edit-${c.id}`}
                      onClick={() => openEdit(c)}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      data-testid={`${testIdPrefix}-delete-${c.id}`}
                      onClick={() => removeItem(c.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {items.length === 0 && (
          <p className="text-center text-gray-500 py-8">
            {debouncedQuery || restaurantFilter !== 'all'
              ? 'No categories match your filters'
              : emptyHint || 'No categories found'}
          </p>
        )}
      </div>

      {/* PAGINATION */}
      <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
        <div data-testid={`${testIdPrefix}-pagination-summary`}>
          Page <span className="font-semibold text-gray-900">{page}</span> of {totalPages}
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid={`${testIdPrefix}-prev-page`}
          >
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid={`${testIdPrefix}-next-page`}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};

/* -------------------------------------------------------------------- */
/*  Page wrapper with two tabs                                          */
/* -------------------------------------------------------------------- */
const Categories = () => {
  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="mb-6">
        <h1
          className="text-3xl font-bold"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Categories
        </h1>
        <p className="text-gray-600 text-sm mt-1">
          Manage admin-level categories and restaurant-level menu categories.
        </p>
      </div>

      <Tabs defaultValue="admin" className="w-full">
        <TabsList
          className="mb-6 bg-orange-50"
          data-testid="categories-tabs-list"
        >
          <TabsTrigger
            value="admin"
            data-testid="tab-admin-categories"
            className="data-[state=active]:bg-white data-[state=active]:text-orange-700"
          >
            Categories
          </TabsTrigger>
          <TabsTrigger
            value="restaurant"
            data-testid="tab-restaurant-categories"
            className="data-[state=active]:bg-white data-[state=active]:text-orange-700"
          >
            Restaurant Categories
          </TabsTrigger>
        </TabsList>

        <TabsContent value="admin">
          <CategoryList
            endpoint="/admin-categories"
            withRestaurant={false}
            testIdPrefix="admin-cat"
            title="Admin Categories"
            subtitle="Global categories created by admin (no restaurant required)."
            emptyHint="No admin categories yet. Click Add Category to create one."
          />
        </TabsContent>

        <TabsContent value="restaurant">
          <CategoryList
            endpoint="/categories"
            withRestaurant={true}
            withStatusToggle={true}
            testIdPrefix="rest-cat"
            title="Restaurant Categories"
            subtitle="Menu categories scoped to a specific restaurant."
            emptyHint="No restaurant categories yet."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Categories;
